class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    // Retrieve selectors from attributes
    this.productsContainerSelector = this.getAttribute('data-products-container');
    this.paginationSelector = this.getAttribute('data-pagination-selector');
    this.nextPageSelector = this.getAttribute('data-next-page-selector');

    // Retrieve observer configurations
    const thresholdAttr = this.getAttribute('data-observer-threshold');
    const rootMarginAttr = this.getAttribute('data-observer-root-margin');
    this.allPagesLoaded = false;
    // Parse threshold; default to 0.1 if not provided or invalid
    this.threshold = thresholdAttr !== null ? parseFloat(thresholdAttr) : 0.1;
    if (isNaN(this.threshold) || this.threshold < 0 || this.threshold > 1) {
      console.warn(
        `Invalid data-observer-threshold="${thresholdAttr}" provided. Falling back to 0.1.`
      );
      this.threshold = 0.1;
    }

    // Use provided rootMargin or default to '0px'
    this.rootMargin = rootMarginAttr || '0px';

    // Select relevant elements within the custom element
    this.productsContainerEl = this.querySelector(this.productsContainerSelector);
    this.paginationEl = this.querySelector(this.paginationSelector);
    this.loadingIndicator = this.querySelector('.loading-overlay-wrapper');

    // Validate essential elements
    if (!this.productsContainerEl || !this.paginationEl) {
      console.error('Wrong configuration for infinite scroll');
      this.loadingIndicator.classList.add('hidden');
      return;
    }

    try {
      // Initialize the IntersectionObserver with the configured threshold and rootMargin
      this.observer = this.registerObserver(this.loadNextPage.bind(this));
      this.observer.observe(this.paginationEl);
      this.paginationEl.style.visibility = 'hidden';
    } catch (error) {
      console.error('Error initializing observer:', error);
    }
  }

  registerObserver(callback) {
    let callbackIsRunning = false;
    const options = {
      threshold: this.threshold,
      rootMargin: this.rootMargin,
    };
    return new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !callbackIsRunning) {
        callbackIsRunning = true;
        callback()
          .then(() => (callbackIsRunning = false))
          .catch(() => (callbackIsRunning = false));
      }
    }, options);
  }

  loadNextPage() {
    if (this.allPagesLoaded) {
      return Promise.resolve();
    }

    const nextPageEl = this.paginationEl.querySelector(this.nextPageSelector);
    if (!nextPageEl) {
      this.observer.disconnect();
      return Promise.resolve();
    }
    const nextPageUrl = nextPageEl.getAttribute('href');
    if (!nextPageUrl) {
      console.warn('Infinite scroll: Next page URL is missing.');
      this.observer.disconnect();
      return Promise.resolve();
    }

    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('hidden');
    }

    return fetch(nextPageUrl)
      .then((result) => {
        if (this.loadingIndicator) {
          this.loadingIndicator.classList.add('hidden');
        }
        if (!result.ok) {
          throw new Error(result.statusText);
        }
        return result.text();
      })
      .then(this.handleNextPage.bind(this))
      .catch((error) => {
        console.error('Error loading next page:', error);
        if (this.loadingIndicator) {
          this.loadingIndicator.classList.add('hidden');
        }
        // Optionally, you can implement retry logic here
      });
  }

  handleNextPage(nextPageContent) {
    const nextPageHtml = new DOMParser().parseFromString(nextPageContent, 'text/html');
    const nextPageProducts = nextPageHtml.querySelector(this.productsContainerSelector);

    if (!nextPageProducts) {
      console.warn('Infinite scroll: Could not find any products in next page');
      return;
    }
    this.productsContainerEl.insertAdjacentHTML('beforeend', nextPageProducts.innerHTML);

    const nextPagePagination = nextPageHtml.querySelector(this.paginationSelector);
    if (!nextPagePagination) {
      console.warn('Infinite scroll: Could not find any pagination in next page');
      this.observer.disconnect(); // No more pages to load
      return;
    }
    this.paginationEl.innerHTML = nextPagePagination.innerHTML;

    // Re-observe the updated pagination element
    this.observer.observe(this.paginationEl);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
